import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { PHONE_TEL } from "@/config/site";
import { useGeolocation } from "@/hooks/useGeolocation";
import { formatApiErrorDetail } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useShareLocationForm — encapsulates all state and business logic for the
 * "Request Emergency Tow" modal:
 *   - form fields (phone, address, vehicle)
 *   - browser Geolocation via useGeolocation
 *   - send-to-dispatch POST /api/contact
 *   - copy-map-link with a transient "copied" flag
 *   - derived values: mapsUrl, smsBody, smsHref, canSend
 *
 * Returned object is deliberately flat so the dialog component stays
 * declarative and free of business logic.
 */
export function useShareLocationForm() {
  const gps = useGeolocation();
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ phone: "", address: "", vehicle: "" });
  const [send, setSend] = useState({ state: "idle", error: "" });

  const reset = useCallback(() => {
    gps.reset();
    setSend({ state: "idle", error: "" });
    setCopied(false);
    // Deliberately do NOT clear the form on reopen — user may retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapsUrl = gps.coords
    ? `https://www.google.com/maps?q=${gps.coords.lat.toFixed(6)},${gps.coords.lng.toFixed(6)}`
    : "";

  const smsBody = useMemo(() => {
    const parts = ["I need a tow."];
    if (gps.coords) {
      parts.push(`My live location: ${mapsUrl} (~${Math.round(gps.coords.accuracy)}m accuracy)`);
    }
    if (form.address.trim()) parts.push(`Address / landmark: ${form.address.trim()}`);
    if (form.vehicle.trim()) parts.push(`Vehicle: ${form.vehicle.trim()}`);
    return parts.join("\n");
  }, [gps.coords, mapsUrl, form.address, form.vehicle]);

  const smsHref = `sms:${PHONE_TEL}?&body=${encodeURIComponent(smsBody)}`;

  const canSend =
    (Boolean(gps.coords) || form.address.trim().length > 3) && form.phone.trim().length > 4;

  const copyMaps = useCallback(async () => {
    if (!mapsUrl) return;
    try {
      await navigator.clipboard.writeText(mapsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // Some browsers block clipboard writes when the doc isn't focused or on
      // non-secure origins. Log in dev; stay silent in prod. UX unaffected.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Clipboard copy failed:", err);
      }
    }
  }, [mapsUrl]);

  const sendToDispatch = useCallback(async () => {
    if (!canSend) return;
    setSend({ state: "sending", error: "" });

    const locationLines = [];
    if (gps.coords) {
      locationLines.push(`GPS: ${mapsUrl}`);
      locationLines.push(`Lat/Lng: ${gps.coords.lat.toFixed(6)}, ${gps.coords.lng.toFixed(6)}`);
      locationLines.push(`Accuracy: ~${Math.round(gps.coords.accuracy)}m`);
    }
    if (form.address.trim()) locationLines.push(`Address / landmark: ${form.address.trim()}`);
    if (form.vehicle.trim()) locationLines.push(`Vehicle: ${form.vehicle.trim()}`);

    const message =
      "URGENT — customer requested a tow from the website.\n\n" +
      locationLines.join("\n") +
      `\n\nCallback phone: ${form.phone.trim()}\n\nPlease dispatch and call the customer back ASAP.`;

    try {
      await axios.post(`${API}/contact`, {
        name: "Emergency Tow Request",
        email: "dispatch-inbox@bmtowingrecovery.com",
        phone: form.phone.trim(),
        service: "24/7 Emergency Towing",
        subject: gps.coords
          ? "LIVE GPS + Request — customer needs a tow"
          : "TOW REQUEST — customer needs a tow",
        message,
      });
      setSend({ state: "sent", error: "" });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = formatApiErrorDetail(raw) || "Couldn't reach dispatch. Please call us directly.";
      setSend({ state: "error", error: msg });
    }
  }, [canSend, gps.coords, mapsUrl, form.address, form.vehicle, form.phone]);

  const updateField = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  return {
    gps,
    form,
    updateField,
    send,
    copied,
    mapsUrl,
    smsHref,
    canSend,
    copyMaps,
    sendToDispatch,
    reset,
  };
}
